import { useCallback, useEffect, useRef, useState } from 'react'
import { FRAMES, LESSON_TITLE } from '../../model/frames.js'
import { D } from '../../model/tiles.js'
import { Tile } from '../../tiles/index.js'
import {
  loadFrame,
  blankFrame,
  serializeHand,
  parseNotation,
  SEAT_ORDER,
  ZONES,
} from '../../model/editorModel.js'
import FrameRail from './FrameRail.jsx'
import SeatRow from './SeatRow.jsx'
import TilePalette from './TilePalette.jsx'
import Inspector from './Inspector.jsx'

export default function EditorScreen({ initialFrames, lessonTitle, saveUrl, backUrl, previewUrl, view }) {
  const [frames, setFrames] = useState(() => initialFrames ?? FRAMES.map(loadFrame))
  const [i, setI] = useState(0)
  const [sel, setSel] = useState({ seat: 'hero', zone: 'hand' })
  const [mode, setMode] = useState('remove')
  const [notation, setNotation] = useState(() => serializeHand(frames[0].seats.hero.hand))
  const [showJson, setShowJson] = useState(false)
  const [jsonText, setJsonText] = useState('')
  const [jsonErr, setJsonErr] = useState('')
  const [histLen, setHistLen] = useState(0)
  const [saveNote, setSaveNote] = useState(null)

  const hist = useRef([])
  const lastLabel = useRef('')
  const lastSnap = useRef(0)
  const saveTimer = useRef(null)

  const frame = frames[i]
  const seat = frame.seats[sel.seat]

  useEffect(() => {
    setNotation(serializeHand(frames[i].seats[sel.seat].hand))
  }, [frames, i, sel])

  function snap(label) {
    const now = Date.now()
    if (label === lastLabel.current && now - lastSnap.current < 900) {
      lastSnap.current = now
      return
    }
    hist.current.push(JSON.stringify({ f: frames, i }))
    if (hist.current.length > 60) hist.current.shift()
    lastLabel.current = label
    lastSnap.current = now
    setHistLen(hist.current.length)
  }

  const doUndo = useCallback(() => {
    const s = hist.current.pop()
    if (!s) return
    const o = JSON.parse(s)
    setFrames(o.f)
    lastLabel.current = ''
    setHistLen(hist.current.length)
    setI(Math.min(o.i, o.f.length - 1))
  }, [])

  useEffect(() => {
    function onKeydown(e) {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') {
        e.preventDefault()
        doUndo()
      }
    }
    window.addEventListener('keydown', onKeydown)
    return () => window.removeEventListener('keydown', onKeydown)
  }, [doUndo])

  function toggleJson() {
    if (showJson) {
      setShowJson(false)
    } else {
      setJsonText(JSON.stringify(frames, null, 1))
      setJsonErr('')
      setShowJson(true)
    }
  }

  function onJsonText(e) {
    setJsonText(e.target.value)
    setJsonErr('')
  }

  function applyJson() {
    try {
      const p = JSON.parse(jsonText)
      if (!Array.isArray(p) || !p.length || !p[0].seats) throw new Error('expected an array of frames with .seats')
      snap('json')
      setFrames(p)
      setI(0)
      setShowJson(false)
    } catch (err) {
      setJsonErr('✕ ' + err.message)
    }
  }

  function update(label, mutator) {
    if (saveTimer.current) {
      clearTimeout(saveTimer.current)
      saveTimer.current = null
    }
    setSaveNote(null)
    snap(label)
    mutator(frames)
    setFrames([...frames])
  }

  function edit(arr, idx) {
    if (mode === 'mark') arr[idx].highlight = !arr[idx].highlight
    else if (mode === 'rotate') arr[idx].rotated = !arr[idx].rotated
    else arr.splice(idx, 1)
  }

  function tileClick(seatKey, zone, idx) {
    update('tile', () => edit(frames[i].seats[seatKey][zone], idx))
  }

  function callClick(seatKey, gi, idx) {
    update('call', () => {
      const calls = frames[i].seats[seatKey].calls
      edit(calls[gi], idx)
      if (!calls[gi] || calls[gi].length === 0) calls.splice(gi, 1)
    })
  }

  function newGroup(seatKey) {
    update('group', () => {
      frames[i].seats[seatKey].calls.push([])
    })
  }

  function addTile(code) {
    update('add', () => {
      const f = frames[i]
      if (sel.zone === 'dora') {
        f.doraInd = code
        return
      }
      const s = f.seats[sel.seat]
      if (sel.zone === 'calls') {
        if (!s.calls.length) s.calls.push([])
        let g = s.calls[s.calls.length - 1]
        if (g.length >= 4) {
          s.calls.push([])
          g = s.calls[s.calls.length - 1]
        }
        g.push({ code })
      } else {
        const arr = sel.zone === 'hand' ? s.hand : s.discards
        if (sel.zone === 'hand' && arr.length >= 14) return
        arr.push({ code })
      }
    })
  }

  function field(key, cast) {
    return (e) =>
      update('f-' + key, () => {
        const v = e.target.value
        frames[i][key] = cast ? parseInt(v, 10) || 0 : v
      })
  }

  function seatField(key, cast) {
    return (e) =>
      update('s-' + key, () => {
        const s = frames[i].seats[sel.seat]
        const v = e.target.type === 'checkbox' ? e.target.checked : e.target.value
        s[key] = cast ? parseInt(v, 10) || 0 : v
      })
  }

  function applyNotation() {
    update('notation', () => {
      const s = frames[i].seats[sel.seat]
      s.hand = parseNotation(notation)
      if (!s.revealed) s.revealed = true
    })
  }

  function setUkeire(tiles, note) {
    if (!tiles.length && !note) {
      frames[i].ukeire = null
    } else {
      frames[i].ukeire = { tiles, note }
    }
  }

  function onUkeireTiles(e) {
    const codes = D(e.target.value).map((t) => t.code)
    update('ukeire tiles', () => setUkeire(codes, frames[i].ukeire?.note ?? ''))
  }

  function onUkeireNote(e) {
    update('ukeire note', () => {
      const tiles = (frames[i].ukeire?.tiles || []).slice()
      setUkeire(tiles, e.target.value)
    })
  }

  function onWin(e) {
    update('win', () => {
      frames[i].win = e.target.checked
    })
  }

  async function saveLesson() {
    if (!saveUrl) return
    if (saveTimer.current) clearTimeout(saveTimer.current)
    let res
    try {
      res = await fetch(saveUrl, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content,
          'Accept': 'application/json',
        },
        body: JSON.stringify({ frames }),
      })
    } catch (err) {
      setSaveNote({ text: 'Save failed', ok: false })
      return
    }
    if (res.ok) {
      setSaveNote({ text: 'Saved ✓', ok: true })
      saveTimer.current = setTimeout(() => setSaveNote(null), 2000)
    } else {
      let msg = 'Save failed'
      try {
        const data = await res.json()
        if (data && Array.isArray(data.errors)) msg = data.errors.join(', ')
        else if (data && data.errors) msg = String(data.errors)
      } catch (_) {}
      setSaveNote({ text: msg, ok: false })
    }
  }

  function duplicateFrame() {
    update('dup', () => {
      frames.splice(i + 1, 0, JSON.parse(JSON.stringify(frames[i])))
    })
    setI(i + 1)
  }

  function addBlankFrame() {
    update('blank', () => {
      frames.splice(i + 1, 0, blankFrame(frames[i]))
    })
    setI(i + 1)
  }

  function deleteFrame() {
    if (frames.length <= 1) return
    update('del', () => {
      frames.splice(i, 1)
    })
    setI(Math.min(i, frames.length - 1))
  }

  function moveFrame(k, d) {
    const j = k + d
    if (j < 0 || j >= frames.length) return
    update('move', () => {
      const tmp = frames[k]
      frames[k] = frames[j]
      frames[j] = tmp
    })
    setI((cur) => (cur === k ? j : cur === j ? k : cur))
  }

  const doraSel = sel.zone === 'dora'
  const selLabel =
    'EDITING: ' + (doraSel ? 'DORA INDICATOR' : (seat.name + ' · ' + ZONES[sel.zone]).toUpperCase())
  const selIsOpp = sel.seat !== 'hero'

  return (
    <div className="flex min-h-screen justify-center py-6">
      <div
        className="select-none"
        style={{
          width: 1280,
          boxSizing: 'border-box',
          background: 'var(--color-bg)',
          color: 'var(--color-text)',
          fontFamily: 'var(--font-body)',
          border: '1px solid var(--color-neutral-300)',
          display: 'flex',
          flexDirection: 'column',
          outline: 'none',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '10px 18px',
            borderBottom: '1px solid var(--color-neutral-300)',
            background: '#fff',
          }}
        >
          {backUrl && (
            <a className="btn btn-ghost" href={backUrl} style={{ height: 30, padding: '0 12px', fontSize: 11 }}>
              ← Back
            </a>
          )}
          <span style={{ fontFamily: 'var(--font-heading)', fontSize: 18, fontWeight: 600, letterSpacing: 2 }}>
            TEACHING TABLE
          </span>
          <span
            style={{
              fontFamily: 'var(--font-heading)',
              fontSize: 10,
              fontWeight: 600,
              letterSpacing: 2,
              color: '#fff',
              background: 'var(--color-accent)',
              padding: '3px 9px',
            }}
          >
            EDIT MODE
          </span>
          <span style={{ fontSize: 11, color: 'var(--color-neutral-600)' }}>Lesson · {lessonTitle ?? LESSON_TITLE}</span>
          <span style={{ marginLeft: 'auto' }} />
          <button
            className="btn btn-ghost"
            disabled={histLen === 0}
            onClick={doUndo}
            style={{ height: 30, padding: '0 12px', fontSize: 11 }}
          >
            ↶ Undo{histLen > 0 ? ` (${histLen})` : ''}
          </button>
          <button className="btn btn-secondary" onClick={toggleJson} style={{ height: 30, padding: '0 12px', fontSize: 11 }}>
            Import / Export
          </button>
          {previewUrl ? (
            <a className="btn btn-secondary" href={previewUrl} target="_blank" rel="noreferrer" style={{ height: 30, padding: '0 14px', fontSize: 11 }}>
              Preview
            </a>
          ) : (
            <button className="btn btn-secondary" style={{ height: 30, padding: '0 14px', fontSize: 11 }}>
              Preview
            </button>
          )}
          {saveNote && !saveNote.ok && (
            <span style={{ fontSize: 11, color: 'var(--color-danger, red)' }}>{saveNote.text}</span>
          )}
          <button className="btn btn-primary" onClick={saveLesson} style={{ height: 30, padding: '0 14px', fontSize: 11 }}>
            {saveNote?.ok ? 'Saved ✓' : 'Save lesson'}
          </button>
        </div>

        {showJson && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
              padding: '12px 18px',
              borderBottom: '1px solid var(--color-neutral-300)',
              background: 'var(--color-accent-100)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span
                style={{
                  fontFamily: 'var(--font-heading)',
                  fontSize: 10,
                  letterSpacing: 3,
                  color: 'var(--color-accent-800)',
                  fontWeight: 600,
                }}
              >
                LESSON JSON — copy out to export, paste + apply to import
              </span>
              <span style={{ fontSize: 11, color: 'var(--color-accent-900)' }}>{jsonErr}</span>
              <span style={{ marginLeft: 'auto' }} />
              <button className="btn btn-secondary" onClick={applyJson} style={{ height: 28, padding: '0 12px', fontSize: 11 }}>
                Apply JSON
              </button>
              <button className="btn btn-ghost" onClick={toggleJson} style={{ height: 28, padding: '0 12px', fontSize: 11 }}>
                Close
              </button>
            </div>
            <textarea
              value={jsonText}
              onChange={onJsonText}
              rows={9}
              spellCheck={false}
              style={{
                width: '100%',
                boxSizing: 'border-box',
                border: '1px solid var(--color-accent-400)',
                padding: '8px 10px',
                fontSize: 11,
                lineHeight: 1.45,
                fontFamily: 'ui-monospace,monospace',
                background: '#fff',
                color: 'var(--color-text)',
                resize: 'vertical',
              }}
            />
          </div>
        )}

        <div style={{ display: 'flex', alignItems: 'stretch' }}>
          <FrameRail
            frames={frames}
            current={i}
            onSelect={(k) => setI(k)}
            onMove={moveFrame}
            onDuplicate={duplicateFrame}
            onBlank={addBlankFrame}
            onDelete={deleteFrame}
          />

          <div style={{ flex: 1, padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 14,
                padding: '7px 12px',
                border: '1px solid var(--color-accent-300)',
                background: 'var(--color-accent-100)',
                fontSize: 11,
              }}
            >
              <span
                style={{
                  fontFamily: 'var(--font-heading)',
                  fontSize: 9,
                  letterSpacing: 2,
                  color: 'var(--color-accent-800)',
                  fontWeight: 600,
                }}
              >
                ROUND
              </span>
              <span style={{ fontFamily: 'var(--font-heading)', fontWeight: 600, fontSize: 15, letterSpacing: 1.5 }}>
                {frame.round}
              </span>
              <span style={{ color: 'var(--color-accent-900)', textTransform: 'uppercase', letterSpacing: 1 }}>
                Wall {frame.wall} · Honba {frame.honba} · Sticks {frame.sticks}
              </span>
              <span
                onClick={() => setSel({ seat: 'hero', zone: 'dora' })}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  fontSize: 9.5,
                  letterSpacing: 2,
                  fontWeight: 600,
                  cursor: 'pointer',
                  padding: '3px 8px',
                  color: 'var(--color-accent-800)',
                  border: '1px ' + (doraSel ? 'solid var(--color-accent)' : 'dashed var(--color-accent-400)'),
                  background: doraSel ? '#fff' : 'transparent',
                }}
              >
                DORA
                <Tile tile={{ code: frame.doraInd }} width={20} highlightColor="var(--color-accent)" />
              </span>
              <span
                style={{
                  marginLeft: 'auto',
                  fontSize: 9.5,
                  letterSpacing: 1.5,
                  color: 'var(--color-accent-700)',
                }}
              >
                {selLabel}
              </span>
            </div>

            {SEAT_ORDER.map((key) => (
              <SeatRow
                key={key}
                seatKey={key}
                seat={frame.seats[key]}
                isHero={key === 'hero'}
                view={view}
                sel={sel}
                onSelectZone={(zone) => setSel({ seat: key, zone })}
                onTileClick={(zone, idx) => tileClick(key, zone, idx)}
                onCallClick={(gi, idx) => callClick(key, gi, idx)}
                onNewGroup={() => newGroup(key)}
              />
            ))}
          </div>

          <div
            style={{
              width: 330,
              flex: 'none',
              borderLeft: '1px solid var(--color-neutral-300)',
              background: '#fff',
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
              padding: '14px 16px',
              boxSizing: 'border-box',
            }}
          >
            <TilePalette mode={mode} onMode={setMode} onAdd={addTile} />
            <Inspector
              seat={seat}
              selIsOpp={selIsOpp}
              view={view}
              frame={frame}
              notation={notation}
              onNotation={(e) => setNotation(e.target.value)}
              onApplyNotation={applyNotation}
              onSeatName={seatField('name')}
              onSeatScore={seatField('score', true)}
              onSeatRiichi={seatField('riichi')}
              onSeatRevealed={seatField('revealed')}
              onRound={field('round')}
              onWall={field('wall', true)}
              onHonba={field('honba', true)}
              onSticks={field('sticks', true)}
              onTitle={field('title')}
              onBody={field('body')}
              onUkeireTiles={onUkeireTiles}
              onUkeireNote={onUkeireNote}
              onWin={onWin}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
